import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { StorageService } from '@services';
import { GrikySkillsService } from '../../modules/griky-skills/services/griky-skills.service';
import { UserLearningPath } from '../../modules/griky-skills/griky-skills.types';

interface Bubble {
  name: string;
  levelLabel: string;
  left: string;
  top: string;
  size: number;
  color: string;      // fondo según nivel (primary-100 .. primary-500)
  textColor: string;  // color de texto legible según el nivel
  font: number;       // tamaño de fuente (px) proporcional al radio
  boxSide: number;    // lado (px) del cuadrado inscrito en el círculo (radio × √2)
  lineHeight: number; // alto de línea (px)
  maxLines: number;   // nº de líneas que caben en la caja (para line-clamp)
  // Métricas en hover: fuente menor + más líneas (mín. 2) para leer mejor el texto recortado
  fontHover: number;
  lineHeightHover: number;
  maxLinesHover: number;
  z: number;
  delay: number;
}

@Component({
  selector: 'app-skills-competencies',
  templateUrl: './skills-competencies.component.html',
  styleUrls: ['./skills-competencies.component.scss']
})
export class SkillsCompetenciesComponent implements OnChanges, OnInit {
  /** Habilidades adquiridas (módulo griky-skills): { skillName, level, ... } */
  @Input() skills: any[] = [];

  /** Resumen de perfil (configurables por el padre) */
  @Input() objective: string = '—';
  @Input() estimatedTime: string = '—';
  @Input() route: string = 'Ruta para senior';

  /** Rol actual del usuario (headline del perfil), distinto del rol objetivo. */
  @Input() currentRole: string = '—';

  /** % de match entre el nivel actual de habilidades y el rol objetivo (null si no hay análisis disponible). */
  @Input() matchPercent: number | null = null;

  /** Cantidad de competencias del rol objetivo que aún no alcanzan el nivel recomendado (null si no hay análisis). */
  @Input() missingCompetencies: number | null = null;

  /** Slug de la empresa para construir los enlaces a griky-skills */
  @Input() companySlug: string = '';

  public paths: UserLearningPath[] = [];

  /** Diseño inicial de la columna de habilidades: 'bubbles' (academia) | 'list' (overview) */
  @Input() view: 'bubbles' | 'list' = 'bubbles';

  /** Si es true, muestra el selector Burbujas/Lista para cambiar de vista (academia). */
  @Input() allowViewToggle: boolean = false;

  /** Si es false, oculta el link "Ver análisis" (perfil público: solo lectura, sin acceso a griky-skills). */
  @Input() showViewAnalysisLink: boolean = true;

  /** Si es false, oculta match/rol actual/rol objetivo/"Cambiar rol objetivo" (perfil público: no aplica ni es editable). */
  @Input() showObjectiveColumn: boolean = true;

  /** Si es false, oculta la tarjeta "Match con tu rol objetivo" (perfil público: no hay análisis propio que mostrar a un visitante). */
  @Input() showMatchCard: boolean = true;

  /** Si es false, oculta el botón "Cambiar rol objetivo" (perfil público: no editable). */
  @Input() showChangeRoleButton: boolean = true;

  /** Si es false, oculta el selector Burbujas/Lista y fuerza la vista de lista (perfil público). */
  @Input() showViewToggleButtons: boolean = true;

  /** Si es false, oculta el título "Competencias" del componente (perfil público: el título va afuera). */
  @Input() showTitle: boolean = true;

  /** Si es true, muestra el título "Competencia por rol" sobre la columna de rol actual/objetivo (perfil público). */
  @Input() showRoleColumnTitle: boolean = false;

  /** Vista actualmente mostrada (el usuario puede cambiarla si allowViewToggle=true). */
  public activeView: 'bubbles' | 'list' = 'bubbles';

  public setActiveView(v: 'bubbles' | 'list'): void {
    this.activeView = v;
  }

  /** Si es true, el componente carga por sí mismo skills + objetivo (uso en overview) */
  @Input() autoLoad: boolean = false;

  /** Módulo griky-skills habilitado para la empresa (igual que en overview) */
  public grikySkillsEnabled = false;

  /** Segmentos del nivel para la vista lista (5 barras) */
  public readonly segments = [1, 2, 3, 4, 5];

  /** Burbujas ya calculadas (estables; se recalculan solo al cambiar los skills) */
  public bubbles: Bubble[] = [];

  constructor(
    private storageService: StorageService,
    private grikySkillsService: GrikySkillsService
  ) {
    const company: any = this.storageService.get('company');
    this.grikySkillsEnabled = !!(company?.permissions?.grikySkillsModule);
    this.company = company;
  }

  private company: any;

  ngOnInit(): void {
    this.activeView = this.showViewToggleButtons ? this.view : 'list';
    if (this.autoLoad && this.company?.id) {
      if (!this.companySlug) { this.companySlug = this.company.slug; }
      this.grikySkillsService.getUserSkills(this.company.id).subscribe({
        next: (skills) => { this.skills = skills || []; this.buildBubbles(); },
        error: () => { this.skills = []; }
      });
      this.grikySkillsService.getObjective(this.company.id).subscribe({
        next: (obj: any) => {
          this.objective = obj?.targetRole || '—';
          this.estimatedTime = this.formatTimeline(obj?.timeline);
        },
        error: () => {}
      });
      this.grikySkillsService.getUserLearningPaths(this.company.id).subscribe({
        next: (paths) => {
          this.paths = paths || [];
          this.route = this.summarizePaths(this.paths);
        },
        error: () => { this.paths = []; this.route = '—'; }
      });
    }
  }

  private summarizePaths(paths: UserLearningPath[]): string {
    const active = paths.filter(p => p.status === 'active');
    if (active.length === 0) { return '—'; }
    if (active.length === 1) {
      return active[0].name || active[0].targetRole || '—';
    }
    return `${active.length} rutas activas`;
  }

  /** Formatea el timeline del backend (ej. "6_months") a texto legible ("6 meses"). */
  private formatTimeline(timeline: string | null | undefined): string {
    if (!timeline) { return '—'; }
    const [num, unit] = timeline.split('_');
    const map: { [key: string]: string } = {
      day: 'día', days: 'días', week: 'semana', weeks: 'semanas',
      month: 'mes', months: 'meses', year: 'año', years: 'años'
    };
    return `${num} ${map[unit] || unit || ''}`.trim();
  }

  /** Todas las habilidades para la vista lista, mayor nivel primero. */
  get sortedSkills(): any[] {
    return [...(this.skills || [])].sort((a, b) => (b?.level || 0) - (a?.level || 0));
  }

  /** Top 3 habilidades (para la vista lista del diseño de overview). */
  get listSkills(): any[] {
    return this.sortedSkills.slice(0, 3);
  }

  /** Agrupa el nivel (0-5) en 3 franjas para la etiqueta de la vista lista. */
  public levelTag(level: number): 'advanced' | 'intermediate' | 'basic' {
    const l = this.clampLevel(level);
    if (l >= 4) { return 'advanced'; }
    if (l >= 2) { return 'intermediate'; }
    return 'basic';
  }

  /** Clases de color de la etiqueta de nivel en la vista lista. */
  public levelTagClass(level: number): string {
    switch (this.levelTag(level)) {
      case 'advanced': return 'bg-emerald-50 text-emerald-700';
      case 'intermediate': return 'bg-blue-50 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  }

  /** Color de una burbuja/leyenda para un nivel dado (misma fórmula que buildBubbles). */
  public legendColor(level: number): string {
    const lvl = this.clampLevel(level);
    return `color-mix(in srgb, var(--v2-color-primary-500) ${this.levelMix[lvl]}%, white)`;
  }

  /** Nº de segmentos rellenos (1..5) según el nivel. */
  public filledSegments(level: number): number {
    return this.clampLevel(level);
  }

  /** Clave i18n de la etiqueta del nivel (para la vista lista). */
  public levelKey(level: number): string {
    return this.levelLabels[this.clampLevel(level)];
  }

  // Posiciones y tamaños fijos (idénticos a la imagen del diseño).
  // Se muestran como máximo 7 burbujas, en este orden.
  private readonly slots = [
    { left: '48%', top: '45%', size: 120, font: 14, z: 120 },
    { left: '78%', top: '30%', size: 88,  font: 12, z: 88 },
    { left: '22%', top: '68%', size: 78,  font: 12, z: 78 },
    { left: '75%', top: '75%', size: 64,  font: 12, z: 64 },
    { left: '20%', top: '25%', size: 56,  font: 10, z: 56 },
    { left: '88%', top: '55%', size: 46,  font: 10, z: 46 },
    { left: '50%', top: '88%', size: 42,  font: 8,  z: 42 },
  ];

  // Etiqueta por nivel (0..5)
  // Devuelve la CLAVE i18n del nivel; se traduce en el template con | translate
  private readonly levelLabels: { [key: number]: string } = {
    0: 'words.no-level',
    1: 'words.beginner',
    2: 'words.basic',
    3: 'words.intermediate',
    4: 'words.advanced',
    5: 'words.expert'
  };

  // Intensidad del color por nivel: % de primary-500 mezclado con blanco.
  // Nivel 5 = 100% (más oscuro), nivel 0 = 12% (muy claro).
  private readonly levelMix: { [key: number]: number } = {
    0: 12,
    1: 22,
    2: 38,
    3: 54,
    4: 72,
    5: 100
  };

  ngOnChanges(): void {
    this.buildBubbles();
  }

  trackByName(_index: number, b: Bubble): string {
    return b.name;
  }

  /** Nivel acotado a 0..5 */
  public clampLevel(level: number): number {
    return Math.min(5, Math.max(0, level ?? 0));
  }

  /**
   * Construye (una sola vez por cambio) las burbujas: mapea hasta 7 habilidades
   * a las posiciones/tamaños fijos del diseño. El color va de primary-100 (nivel 1)
   * a primary-500 (nivel 5); el texto se adapta para ser legible.
   */
  private buildBubbles(): void {
    const list = (this.skills || []).slice(0, this.slots.length);
    this.bubbles = list.map((s, i) => {
      const lvl = this.clampLevel(s?.level);
      const size = this.slots[i].size;
      const metrics = this.computeTextMetrics(size);
      return {
        name: s?.skillName || '',
        levelLabel: this.levelLabels[lvl],
        left: this.slots[i].left,
        top: this.slots[i].top,
        size: size,
        color: `color-mix(in srgb, var(--v2-color-primary-500) ${this.levelMix[lvl]}%, white)`,
        textColor: lvl >= 3 ? '#ffffff' : '#26314C',
        font: metrics.font,
        boxSide: metrics.boxSide,
        lineHeight: metrics.lineHeight,
        maxLines: metrics.maxLines,
        fontHover: metrics.fontHover,
        lineHeightHover: metrics.lineHeightHover,
        maxLinesHover: metrics.maxLinesHover,
        z: this.slots[i].z,
        delay: i * 60
      };
    });
  }

  /**
   * Calcula, según el diámetro de la burbuja, las métricas del texto para que
   * nunca se salga del círculo:
   *  - font: proporcional al radio (clamp 11..18px).
   *  - boxSide: lado del mayor cuadrado inscrito en el círculo = radio × √2.
   *  - maxLines: cuántas líneas caben verticalmente en esa caja.
   * El texto se envuelve dentro de esa caja y se recorta con "…" a maxLines.
   */
  private computeTextMetrics(size: number): {
    font: number; boxSide: number; lineHeight: number; maxLines: number;
    fontHover: number; lineHeightHover: number; maxLinesHover: number;
  } {
    const radius = size / 2;
    // Fuente más pequeña y proporcional al radio (se lee mejor y cabe más texto)
    const font = Math.round(Math.min(15, Math.max(9, radius * 0.22)));
    const boxSide = Math.round(radius * Math.SQRT2);
    const lineHeight = Math.round(font * 1.1);
    const maxLines = Math.max(1, Math.floor(boxSide / lineHeight));

    // En hover: fuente un poco menor → caben más líneas en la misma caja (mín. 2)
    const fontHover = Math.max(8, Math.round(font * 0.85));
    const lineHeightHover = Math.round(fontHover * 1.05);
    const maxLinesHover = Math.max(2, Math.floor(boxSide / lineHeightHover));

    return { font, boxSide, lineHeight, maxLines, fontHover, lineHeightHover, maxLinesHover };
  }
}
